"use client";

import React from "react";
import {Grid, Stack, useMediaQuery} from "@mui/material";
import {BasicInfo} from "@/types/dto/common/basic-info";
import {MediaCardBase} from "@/types/dto/common/media-card";
import {ColletteCarousel} from "@collette-travel/collette-common-controls";
import WistiaVideoCard from "@/components/common/wistia-video-carousel-section/wistia-video-card";
import {chunkArray} from "@/utils/general-util";
import TextModule from "@/components/common/text-module/text-module";

interface WistiaVideoCarouselSectionProps {
  basicInfo: BasicInfo;
  mediaCards: MediaCardBase[];
  moduleRankingPosition: number | string;
}

const WistiaVideoCarouselSection = ({
  basicInfo,
  mediaCards,
  moduleRankingPosition
}: WistiaVideoCarouselSectionProps) => {

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const arrowResolver = () => {
    switch (true) {
      case isMobile:
        return mediaCards.length >= 2;
      case isTablet:
        return mediaCards.length >= 3;
      default:
        return mediaCards.length >= 4;
    }
  };

  const cardsPerPageResolver = () => {
    switch (true) {
      case isMobile:
        return 1;
      case isTablet:
        return 2;
      default:
        return 3;
    }
  };

  return (
    <Stack
      className="destination-page-container"
      component="section"
      data-module-name="Video Media Carousel"
      data-parent-container-type="wistia-video-carousel-section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      data-track="visibility"
      data-testid="wistia-video-carousel-section"
      sx={{
        gap: "2rem",
        alignItems: "center",
        padding: { xs: "4rem 2.4rem", lg: "6rem 10rem" }
      }}
    >
      <TextModule
        basicInfo={basicInfo}
        slotProps={{
          root: {
            sx: {
              padding: 0
            }
          }
        }}
      />
      <ColletteCarousel
        testId="wistia-media-card-carousel"
        variant="full-width"
        arrowPosition={arrowResolver() ? "bottom" : "none"}
      >
        {chunkArray(mediaCards, cardsPerPageResolver()).map((cards) => (
          <Grid container key={`${cards?.[0]?.mediaCardId}-page`} spacing={4}>
            {cards.map((card) => (
              <Grid size={12 / cardsPerPageResolver()} key={card.mediaCardId}>
                <WistiaVideoCard
                  videoData={{
                    id: card.mediaCardId,
                    wistiaVideoId: card.media.wistiaVideoId,
                    title: card.media.wistiaVideoTitle
                  }}
                  subtitle={card.overlayText.subtitle}
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </ColletteCarousel>
    </Stack>
  );
};

export default WistiaVideoCarouselSection;